import { createRoute, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { Check, Sparkles, PartyPopper, Monitor } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { Button } from '../components/ui/Button';
import { useAppMode } from '../context/AppModeContext';
import { useInventoryList } from '../hooks/useInventory';
import { useTranslation } from '../i18n';
import { apiClient } from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Route as rootRoute } from './__root';
import styles from './upgrade.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/upgrade',
  component: UpgradePage,
});

function UpgradePage() {
  const { t } = useTranslation();
  const routerState = useRouterState();
  const success = new URLSearchParams(routerState.location.searchStr).get('success') === '1';
  const { aiTier, aiUsage } = useAppMode();
  const { toast } = useToast();
  const { data: inventoryData } = useInventoryList({ limit: 1 });
  const [loading, setLoading] = useState(false);
  const [desktopLoading, setDesktopLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const itemCount = inventoryData?.total ?? 0;

  async function handleCheckout() {
    setLoading(true);
    try {
      const { checkoutUrl } = await apiClient.createCheckout(billingPeriod);
      window.location.href = checkoutUrl;
    } catch {
      setLoading(false);
      toast(t('upgrade.checkoutError'), 'error');
    }
  }

  async function handleDesktopCheckout() {
    setDesktopLoading(true);
    try {
      const { checkoutUrl } = await apiClient.createDesktopCheckout();
      window.location.href = checkoutUrl;
    } catch {
      setDesktopLoading(false);
      toast(t('upgrade.checkoutError'), 'error');
    }
  }

  const isPro = aiTier === 'pro';

  const comparisonRows = [
    { feature: t('upgrade.featureItems'), free: t('upgrade.freeItems'), pro: t('upgrade.proItems') },
    { feature: t('upgrade.featureVaults'), free: t('upgrade.freeVaults'), pro: t('upgrade.proVaults') },
    { feature: t('upgrade.featureShowroom'), free: t('upgrade.freeShowroom'), pro: t('upgrade.proShowroom') },
    { feature: t('upgrade.featureAi'), free: t('upgrade.freeAi'), pro: t('upgrade.proAi') },
    { feature: t('upgrade.featureExport'), free: 'CSV, JSON', pro: 'CSV, JSON, PDF' },
    { feature: t('upgrade.featureThemes'), free: <Check size={14} strokeWidth={2.5} className={styles.checkIcon} />, pro: <Check size={14} strokeWidth={2.5} className={styles.checkIcon} /> },
    { feature: t('upgrade.featureSets'), free: <Check size={14} strokeWidth={2.5} className={styles.checkIcon} />, pro: <Check size={14} strokeWidth={2.5} className={styles.checkIcon} /> },
    { feature: t('upgrade.featureImages'), free: <Check size={14} strokeWidth={2.5} className={styles.checkIcon} />, pro: <Check size={14} strokeWidth={2.5} className={styles.checkIcon} /> },
  ];

  // Usage bar colors
  const itemPct = Math.min((itemCount / 100) * 100, 100);
  const itemBarColor = itemPct >= 95 ? 'var(--error, #ef4444)' : itemPct >= 80 ? 'var(--warning, #eab308)' : 'var(--success, #22c55e)';

  return (
    <Shell>
      <div className={styles.page}>
        {success && (
          <div className={styles.successBanner}>
            <PartyPopper size={18} strokeWidth={1.75} />
            <div>
              <strong>{t('upgrade.successTitle')}</strong>
              <p>{t('upgrade.successDesc')}</p>
            </div>
          </div>
        )}

        <div className={styles.header}>
          <h1 className={styles.title}>{t('upgrade.title')}</h1>
          <p className={styles.subtitle}>{t('upgrade.subtitle')}</p>
        </div>

        {/* Current plan */}
        <div className={styles.currentPlan}>
          <div className={styles.currentPlanHeader}>
            <span className={styles.currentPlanTitle}>{t('upgrade.currentPlan')}</span>
            <span className={isPro ? styles.tierBadgePro : styles.tierBadge}>
              {isPro ? 'Pro' : t('upgrade.freeBadge')}
            </span>
          </div>

          {!isPro && (
            <div className={styles.usageGrid}>
              <div className={styles.usageMeter}>
                <div className={styles.usageLabel}>
                  <span>{t('upgrade.itemsUsed')}</span>
                  <span className={styles.usageCount}>{itemCount} / 100</span>
                </div>
                <div className={styles.usageBar}>
                  <div
                    className={styles.usageBarFill}
                    style={{ width: `${itemPct}%`, background: itemBarColor }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comparison */}
        <h2 className={styles.comparisonTitle}>{t('upgrade.compareTitle')}</h2>
        <table className={styles.comparisonTable}>
          <thead>
            <tr>
              <th>{t('upgrade.feature')}</th>
              <th>{t('upgrade.freeCol')}</th>
              <th className={styles.proCol}>{t('upgrade.proCol')}</th>
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map(({ feature, free, pro }) => (
              <tr key={feature}>
                <td>{feature}</td>
                <td>{free}</td>
                <td className={styles.proCol}>{pro}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* CTA or Pro status */}
        {isPro ? (
          <div className={styles.proActive}>
            <div className={styles.proActiveTitle}>{t('upgrade.proActiveTitle')}</div>
            <div className={styles.proActiveDesc}>{t('upgrade.proActiveDesc')}</div>
            {aiUsage && (
              <div className={styles.aiUsage}>
                <div className={styles.aiUsageHeader}>
                  <span>{aiUsage.count} / {aiUsage.limit} {t('upgrade.analysesUsed')}</span>
                  <span>{aiUsage.limit - aiUsage.count} {t('upgrade.remaining')}</span>
                </div>
                <div className={styles.aiUsageBar}>
                  <div
                    className={styles.aiUsageBarFill}
                    style={{
                      width: `${Math.min((aiUsage.count / aiUsage.limit) * 100, 100)}%`,
                      background: (aiUsage.count / aiUsage.limit) >= 0.95
                        ? 'var(--error, #ef4444)'
                        : (aiUsage.count / aiUsage.limit) >= 0.8
                        ? 'var(--warning, #eab308)'
                        : 'var(--success, #22c55e)',
                    }}
                  />
                </div>
                {aiUsage.periodEnd && (
                  <div className={styles.aiUsageReset}>
                    {t('upgrade.resetsOn')} {new Date(aiUsage.periodEnd).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.ctaSection}>
            <div className={styles.ctaTitle}>{t('upgrade.ctaTitle')}</div>
            <div className={styles.billingToggle} role="group">
              <button
                className={`${styles.billingOption} ${billingPeriod === 'monthly' ? styles.billingOptionActive : ''}`}
                onClick={() => setBillingPeriod('monthly')}
              >
                {t('pricing.monthly')}
              </button>
              <button
                className={`${styles.billingOption} ${billingPeriod === 'yearly' ? styles.billingOptionActive : ''}`}
                onClick={() => setBillingPeriod('yearly')}
              >
                {t('pricing.yearly')}
              </button>
            </div>
            <div className={styles.ctaPriceRow}>
              <div className={styles.ctaPrice}>
                {billingPeriod === 'monthly' ? '$5' : '$40'}
                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                  {billingPeriod === 'monthly' ? ' /mo' : ' /yr'}
                </span>
              </div>
              {billingPeriod === 'yearly' && (
                <span className={styles.saveBadge}>{t('pricing.saveBadge')}</span>
              )}
            </div>
            <div className={styles.ctaPeriod}>
              {billingPeriod === 'monthly' ? t('upgrade.ctaPeriodMonthly') : t('upgrade.ctaPeriodYearly')}
            </div>
            <Button onClick={handleCheckout} disabled={loading}>
              <Sparkles size={14} strokeWidth={2} style={{ marginRight: '0.375rem' }} />
              {loading ? t('upgrade.ctaLoading') : t('upgrade.ctaButton')}
            </Button>
          </div>
        )}

        {/* Desktop license */}
        <div className={styles.desktopDivider}>or</div>
        <div className={styles.desktopSection}>
          <Monitor size={20} strokeWidth={1.75} style={{ marginBottom: '0.75rem', color: 'var(--text-muted)' }} />
          <div className={styles.desktopTitle}>{t('upgrade.desktopTitle')}</div>
          <div className={styles.desktopDesc}>{t('upgrade.desktopDesc')}</div>
          <div className={styles.desktopPrice}>
            {t('upgrade.desktopPrice')}
            <span className={styles.desktopPriceSuffix}> {t('upgrade.desktopPriceSuffix')}</span>
          </div>
          <div className={styles.desktopPeriod} />
          <Button variant="secondary" onClick={handleDesktopCheckout} disabled={desktopLoading}>
            <Monitor size={14} strokeWidth={2} style={{ marginRight: '0.375rem' }} />
            {desktopLoading ? t('upgrade.desktopLoading') : t('upgrade.desktopCta')}
          </Button>
        </div>
      </div>
    </Shell>
  );
}
